import { useCallback, useEffect, useRef, useState } from 'react';
import type { CommsSentListResponse, CommsSentRow, CommsSentSummary } from '@ucp/web/lib';
import { NotSignedInError, useApiFetch } from './useApiFetch';

/**
 * Communications "Sent" activity hook (PRO-0620). Fetches the counts-only,
 * school-scoped `GET /comms/sent` list + summary for the active school and exposes
 * a range control + keyset "load more" paging. COUNTS ONLY — the response carries
 * no recipient name/address/phone or message body (SEC-8); this hook never derives
 * or stores any.
 *
 * `range` (days) is persisted to `localStorage` with a safe fallback (DS-5): a
 * blocked/again-unavailable store degrades to the in-memory default rather than
 * throwing. The server clamps `range`/`limit`, so a tampered stored value cannot
 * widen the read.
 */

const RANGE_STORAGE_KEY = 'ucp.comms.sent.range';
const RANGE_DEFAULT = 30;
const RANGE_OPTIONS = [7, 30, 90] as const;
const PAGE_LIMIT = 25;

export type SentActivityStatus = 'loading' | 'ready' | 'error';

export interface SentActivityState {
  readonly summary: CommsSentSummary | undefined;
  readonly rows: readonly CommsSentRow[];
  readonly status: SentActivityStatus;
  readonly error: string | null;
  readonly hasMore: boolean;
  readonly loadingMore: boolean;
  readonly range: number;
  readonly rangeOptions: readonly number[];
  setRange(days: number): void;
  loadMore(): void;
  retry(): void;
}

function readStoredRange(): number {
  try {
    const raw = window.localStorage.getItem(RANGE_STORAGE_KEY);
    const parsed = raw === null ? NaN : Number.parseInt(raw, 10);
    return (RANGE_OPTIONS as readonly number[]).includes(parsed) ? parsed : RANGE_DEFAULT;
  } catch {
    return RANGE_DEFAULT;
  }
}

function writeStoredRange(days: number): void {
  try {
    window.localStorage.setItem(RANGE_STORAGE_KEY, String(days));
  } catch {
    // Non-fatal (DS-5): the range still applies in memory this session.
  }
}

export function useSentActivity(): SentActivityState {
  const apiFetch = useApiFetch();
  const [range, setRangeState] = useState<number>(() => readStoredRange());
  const [summary, setSummary] = useState<CommsSentSummary | undefined>(undefined);
  const [rows, setRows] = useState<readonly CommsSentRow[]>([]);
  const [status, setStatus] = useState<SentActivityStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  const cursorRef = useRef<string | null>(null);
  cursorRef.current = nextCursor;
  // `loadMore` is invoked from an onClick, so it can't rely on effect cleanup to
  // cancel. These refs let a completed paged fetch bail out of applying its result
  // once the component has unmounted (`mountedRef`) or the list has been reloaded /
  // superseded by a range change or retry (`loadGenerationRef`), preventing stale
  // state updates and appends against a freshly reloaded list.
  const mountedRef = useRef(true);
  const loadGenerationRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      loadGenerationRef.current += 1; // supersede any in-flight loadMore
    };
  }, []);

  const fetchPage = useCallback(
    async (cursor: string | null): Promise<CommsSentListResponse | null> => {
      const params = new URLSearchParams({ range: String(range), limit: String(PAGE_LIMIT) });
      if (cursor) {
        params.set('cursor', cursor);
      }
      const response = await apiFetch(`/comms/sent?${params.toString()}`);
      if (!response) {
        return null; // interactive redirect in flight
      }
      if (response.status !== 200) {
        throw new Error(`HTTP ${response.status}`);
      }
      return (await response.json()) as CommsSentListResponse;
    },
    [apiFetch, range],
  );

  // Initial + range-change + retry load (replaces the list).
  useEffect(() => {
    let cancelled = false;
    // A reload supersedes any in-flight loadMore so its page can't append onto the
    // list this effect is about to replace.
    loadGenerationRef.current += 1;
    setStatus('loading');
    setError(null);
    (async () => {
      try {
        const data = await fetchPage(null);
        if (cancelled || !data) {
          return;
        }
        setSummary(data.summary);
        setRows(data.rows);
        setNextCursor(data.nextCursor);
        setStatus('ready');
      } catch (err) {
        if (cancelled) {
          return;
        }
        setError(
          err instanceof NotSignedInError ? err.message : 'Could not load recent sends just now.',
        );
        setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchPage, reloadNonce]);

  const setRange = useCallback((days: number) => {
    setRangeState(days);
    writeStoredRange(days);
  }, []);

  const loadMore = useCallback(() => {
    const cursor = cursorRef.current;
    if (!cursor || loadingMore) {
      return;
    }
    const generation = loadGenerationRef.current;
    const superseded = () => !mountedRef.current || generation !== loadGenerationRef.current;
    setLoadingMore(true);
    (async () => {
      try {
        const data = await fetchPage(cursor);
        if (superseded() || !data) {
          return;
        }
        setRows((prev) => [...prev, ...data.rows]);
        setNextCursor(data.nextCursor);
      } catch {
        if (superseded()) {
          return;
        }
        // Keep the loaded rows; surface a transient error without wiping the page.
        setError('Could not load more sends just now.');
      } finally {
        // Clear the transient flag whenever still mounted — including when a reload
        // superseded this fetch — so the button never sticks in its loading state.
        if (mountedRef.current) {
          setLoadingMore(false);
        }
      }
    })();
  }, [fetchPage, loadingMore]);

  const retry = useCallback(() => setReloadNonce((n) => n + 1), []);

  return {
    summary,
    rows,
    status,
    error,
    hasMore: nextCursor !== null,
    loadingMore,
    range,
    rangeOptions: RANGE_OPTIONS,
    setRange,
    loadMore,
    retry,
  };
}
